import { useEffect, useId, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { format } from "date-fns";
import { z } from "zod";
import { CalendarIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DialogFooter } from "@/components/ui/dialog";
import { Button } from "@akuna/ui";
import { Input } from "@akuna/ui";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@akuna/ui";
import { DateFieldPopover } from "@/components/ui/date-field-popover";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter as AlertDialogFooterPrimitive,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { cn } from "@/lib/utils";
import { petApplicationPlanSchema, type PetApplicationPlanInput } from "@/lib/validations/application-plan";
import { useApplications } from "@/hooks/useApplications";
import {
  useCreatePetApplicationPlan,
  useDeletePetApplicationPlan,
  useUpdatePetApplicationPlan,
} from "@/hooks/usePetApplicationPlans";
import { createOrUpdateVaccinationAppointment, useUpdatePetApplication } from "@/hooks/usePetApplications";
import type { VaccineOption } from "@/hooks/useApplications";
import { combineDateAndTime, isValidTimeHHmm, setDateOnlyNoon } from "@/lib/utils/next-dose";
import { ApplicationAutocomplete } from "./ApplicationAutocomplete";
import { CreateApplicationDialog } from "./CreateApplicationDialog";
import {
  NextApplicationsList,
  type NextApplicationEntry,
} from "./NextApplicationsList";
import type { ApplicationSubmissionMode } from "./applicationSubmission";
import type { PetApplicationPlanFormDraft } from "@/pages/daycare-hotel/reservation/petApplicationPlanDraft";

interface ApplicationPlanFormProps {
  petId: string;
  clinicId: string;
  petSpeciesId?: string;
  appointmentId?: string;
  plan?: {
    id: string;
    item_id: string;
    due_date: string;
    notes?: string | null;
    appointment_id?: string | null;
    source_type?: "manual" | "derived_from_application" | null;
    source_pet_application_id?: string | null;
  } | null;
  onCancel: () => void;
  onSuccess: () => void;
  /** When provided, the delete/cancel/save footer buttons are portaled here instead of rendered inline. Used by modal callers that render their own fixed footer. */
  footerContainer?: HTMLDivElement | null;
  submissionMode?: ApplicationSubmissionMode;
  onDraftSubmit?: (draft: PetApplicationPlanFormDraft) => void;
  onDraftDelete?: (planId: string) => void;
}

const petApplicationPlanSchemaWithAppointment = petApplicationPlanSchema
  .and(
    z.object({
      create_appointment: z.boolean().optional(),
      due_time: z.string().optional(),
    }),
  )
  .superRefine((data, ctx) => {
    if (data.create_appointment !== true) return;

    if (!data.due_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["due_date"],
        message: "vaccines.validation.nextDoseDateRequired",
      });
    }

    if (!data.due_time || !isValidTimeHHmm(data.due_time)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["due_time"],
        message: "vaccines.validation.nextDoseTimeRequired",
      });
    }
  });

type PetApplicationPlanFormInput = PetApplicationPlanInput & {
  create_appointment?: boolean;
  due_time?: string;
};

export function ApplicationPlanForm({
  petId,
  clinicId,
  petSpeciesId,
  appointmentId,
  plan,
  onCancel,
  onSuccess,
  footerContainer = null,
  submissionMode = "immediate",
  onDraftSubmit,
  onDraftDelete,
}: ApplicationPlanFormProps) {
  const { t } = useTranslation("reports");
  const formId = useId();
  const [applicationSearch, setApplicationSearch] = useState("");
  const [createApplicationDialogOpen, setCreateApplicationDialogOpen] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [createdOption, setCreatedOption] = useState<VaccineOption | null>(null);
  const [nextAppEntries, setNextAppEntries] = useState<NextApplicationEntry[]>([]);

  const createPlan = useCreatePetApplicationPlan();
  const updatePlan = useUpdatePetApplicationPlan();
  const deletePlan = useDeletePetApplicationPlan();
  const updateVaccination = useUpdatePetApplication();
  const { data: applications, isLoading: isApplicationsLoading } = useApplications({
    speciesId: petSpeciesId,
    isActive: true,
    clinicId,
    includeItemIds: plan?.item_id ? [plan.item_id] : [],
  });

  const applicationOptions = useMemo(() => {
    const base = applications ?? [];
    if (!createdOption) return base;
    const withoutCreated = base.filter((option) => option.itemId !== createdOption.itemId);
    return [createdOption, ...withoutCreated];
  }, [applications, createdOption]);

  const {
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<PetApplicationPlanFormInput>({
    resolver: zodResolver(petApplicationPlanSchemaWithAppointment),
    defaultValues: {
      pet_id: petId,
      clinic_id: clinicId,
      item_id: null,
      due_date: new Date(),
      notes: "",
      appointment_id: null,
      create_appointment: false,
      due_time: "",
    },
  });

  useEffect(() => {
    const plannedDate = plan?.due_date ? new Date(plan.due_date) : new Date();
    const plannedTime = plan?.appointment_id && plan?.due_date ? format(new Date(plan.due_date), "HH:mm") : "";
    reset({
      pet_id: petId,
      clinic_id: clinicId,
      item_id: plan?.item_id ?? null,
      due_date: plannedDate,
      notes: plan?.notes ?? "",
      appointment_id: plan?.appointment_id ?? null,
      create_appointment: Boolean(plan?.appointment_id),
      due_time: plannedTime,
    });
    setApplicationSearch("");
    setNextAppEntries([]);
  }, [appointmentId, clinicId, petId, plan, reset]);

  const selectedItemId = watch("item_id");
  const selectedAutocompleteValue = selectedItemId ? `item:${selectedItemId}` : "";
  const createAppointment = watch("create_appointment");
  const selectedApplicationOption = useMemo(
    () => applicationOptions.find((option) => option.itemId === selectedItemId) ?? null,
    [applicationOptions, selectedItemId],
  );

  useEffect(() => {
    if (!selectedItemId) {
      setApplicationSearch("");
      return;
    }

    const selectedOption = applicationOptions.find((option) => option.itemId === selectedItemId);
    if (!selectedOption) return;

    setApplicationSearch(selectedOption.label || selectedOption.rawName || "");
  }, [applicationOptions, selectedItemId]);

  const onSubmit = async (data: PetApplicationPlanFormInput) => {
    const itemId = data.item_id?.trim();
    if (!itemId) return;

    const dateOnly = setDateOnlyNoon(data.due_date);
    if (submissionMode === "deferred") {
      onDraftSubmit?.({
        pet_id: petId,
        clinic_id: clinicId,
        item_id: itemId,
        due_date: dateOnly.toISOString(),
        notes: data.notes?.trim() || null,
        appointment_id: null,
        source_type: plan?.source_type ?? "manual",
        source_pet_application_id: plan?.source_pet_application_id ?? null,
        item: selectedApplicationOption
          ? { name: selectedApplicationOption.label }
          : null,
      });
      onSuccess();
      return;
    }

    const appointmentDate = data.create_appointment
      ? combineDateAndTime(dateOnly, data.due_time ?? "")
      : dateOnly;

    const linkedAppointmentId = data.create_appointment
      ? await createOrUpdateVaccinationAppointment({
        petId,
        clinicId,
        nextDoseDate: appointmentDate,
        vaccineItemId: itemId,
        nextApplicationItemId: itemId,
        existingAppointmentId: plan?.appointment_id ?? null,
        notes: data.notes?.trim() || null,
      })
      : null;

    if (plan?.source_type === "derived_from_application" && plan.source_pet_application_id && !plan.id) {
      await updateVaccination.mutateAsync({
        id: plan.source_pet_application_id,
        has_next_dose: true,
        next_dose_date: appointmentDate.toISOString(),
        next_application_item_id: itemId,
        appointment_id: linkedAppointmentId ?? null,
        notes: data.notes?.trim() || null,
      });
    } else if (plan?.id) {
      await updatePlan.mutateAsync({
        id: plan.id,
        item_id: itemId,
        due_date: appointmentDate.toISOString(),
        notes: data.notes?.trim() || null,
        appointment_id: linkedAppointmentId ?? null,
        next_applications: nextAppEntries,
      });
    } else {
      await createPlan.mutateAsync({
        pet_id: petId,
        clinic_id: clinicId,
        item_id: itemId,
        due_date: appointmentDate.toISOString(),
        notes: data.notes?.trim() || null,
        appointment_id: linkedAppointmentId ?? null,
        next_applications: nextAppEntries,
      });
    }

    onSuccess();
  };

  const handleConfirmDelete = async () => {
    if (!plan) return;

    try {
      if (submissionMode === "deferred") {
        if (plan.id) onDraftDelete?.(plan.id);
        setShowDeleteDialog(false);
        onSuccess();
        return;
      }

      if (plan.source_type === "derived_from_application" && plan.source_pet_application_id) {
        await updateVaccination.mutateAsync({
          id: plan.source_pet_application_id,
          has_next_dose: false,
          next_dose_date: null,
          next_application_item_id: null,
          create_appointment: false,
          cancelAppointment: true,
        });
      }
      if (plan.id) {
        await deletePlan.mutateAsync({ id: plan.id });
      }
      setShowDeleteDialog(false);
      onSuccess();
    } catch (error) {
      console.error("Error deleting application plan:", error);
    }
  };

  return (
    <>
      <form id={formId} className="space-y-4" onSubmit={handleSubmit(onSubmit)}>
        <div className="space-y-2">
          <Label>{t("vaccines.planForm.application")}</Label>
          <ApplicationAutocomplete
            options={applicationOptions}
            isLoading={isApplicationsLoading}
            value={applicationSearch}
            selectedValue={selectedAutocompleteValue}
            onChange={(value) => {
              setApplicationSearch(value);
              const current = applicationOptions.find((option) => option.value === selectedAutocompleteValue);
              if (!current) return;

              const normalizedValue = value.toLowerCase().trim();
              const normalizedCurrent = (current.label ?? current.rawName ?? "").toLowerCase().trim();
              if (normalizedValue !== normalizedCurrent) {
                setValue("item_id", null, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
              }
            }}
            onSelectOption={(option, displayName) => {
              setApplicationSearch(displayName);
              setValue("item_id", option.itemId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
            }}
            placeholder={t("vaccines.form.applicationPlaceholder")}
            onOpenCreateDialog={submissionMode === "immediate"
              ? () => setCreateApplicationDialogOpen(true)
              : undefined}
            popoverPortalled={false}
          />
          {selectedApplicationOption?.isDeleted ? (
            <p className="text-xs text-muted-foreground">
              {t("vaccines.autocomplete.deletedItemDescription")}
            </p>
          ) : null}
          {errors.item_id ? <p className="text-sm text-destructive">{t(String(errors.item_id.message))}</p> : null}
        </div>

        <div className="space-y-2">
          <Label>{t("vaccines.planForm.dueDate")}</Label>
          <Controller
            control={control}
            name="due_date"
            render={({ field }) => (
              <DateFieldPopover
                trigger={
                  <Button
                    type="button"
                    variant="outline"
                    className={cn("w-full justify-start text-left font-normal", !field.value && "text-muted-foreground")}
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {field.value ? format(field.value, "dd/MM/yyyy") : t("vaccines.form.selectDate")}
                  </Button>
                }
                selected={field.value}
                onSelect={(date) => date && field.onChange(date)}
              />
            )}
          />
          {errors.due_date ? <p className="text-sm text-destructive">{t(String(errors.due_date.message))}</p> : null}
        </div>

        {submissionMode === "immediate" ? <Controller
          name="create_appointment"
          control={control}
          render={({ field }) => (
            <div className="flex items-center justify-between rounded-md border border-border/60 px-3 py-2">
              <span className="text-sm text-muted-foreground">{t("vaccines.form.createAppointment")}</span>
              <Switch checked={Boolean(field.value)} onCheckedChange={field.onChange} />
            </div>
          )}
        /> : null}

        {createAppointment ? (
          <div className="space-y-2">
            <Label htmlFor="plan-due-time">{t("vaccines.form.time")} *</Label>
            <Controller
              name="due_time"
              control={control}
              render={({ field }) => (
                <Input
                  id="plan-due-time"
                  type="time"
                  value={field.value ?? ""}
                  onChange={(event) => field.onChange(event.target.value)}
                />
              )}
            />
            {errors.due_time?.message ? <p className="text-sm text-destructive">{t(String(errors.due_time.message))}</p> : null}
          </div>
        ) : null}

        <div className="space-y-2">
          <Label htmlFor="plan-notes">{t("vaccines.planForm.notes")}</Label>
          <Controller
            control={control}
            name="notes"
            render={({ field }) => (
              <Textarea
                id="plan-notes"
                value={field.value ?? ""}
                onChange={field.onChange}
                placeholder={t("vaccines.planForm.notesPlaceholder")}
              />
            )}
          />
        </div>

        {(() => {
          const footerButtons = (
            <>
              <div>
                {plan?.id ? (
                  <Button
                    type="button"
                    variant="destructive"
                    onClick={() => setShowDeleteDialog(true)}
                    disabled={deletePlan.isPending || updateVaccination.isPending}
                  >
                    {deletePlan.isPending || updateVaccination.isPending
                      ? t("vaccines.planForm.deleting")
                      : t("vaccines.planForm.delete")}
                  </Button>
                ) : null}
              </div>
              <div className="flex gap-2">
              <Button type="button" variant="outline" onClick={onCancel}>
                {t("vaccines.planForm.cancel")}
              </Button>
              <Button
                type="submit"
                form={formId}
                disabled={createPlan.isPending || updatePlan.isPending || deletePlan.isPending || updateVaccination.isPending}
              >
                {createPlan.isPending || updatePlan.isPending
                  ? t("vaccines.planForm.saving")
                  : plan?.id
                    ? t("vaccines.planForm.update")
                    : t("vaccines.planForm.save")}
              </Button>
              </div>
            </>
          );

          return footerContainer ? (
            createPortal(footerButtons, footerContainer)
          ) : (
            <DialogFooter className="flex w-full justify-between gap-2">{footerButtons}</DialogFooter>
          );
        })()}
      </form>

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("vaccines.planForm.deleteDialog.title")}</AlertDialogTitle>
            <AlertDialogDescription>
              {t("vaccines.planForm.deleteDialog.description")}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooterPrimitive>
            <AlertDialogCancel disabled={deletePlan.isPending || updateVaccination.isPending}>
              {t("vaccines.planForm.deleteDialog.cancel")}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmDelete}
              disabled={deletePlan.isPending || updateVaccination.isPending}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePlan.isPending || updateVaccination.isPending
                ? t("vaccines.planForm.deleteDialog.deleting")
                : t("vaccines.planForm.deleteDialog.confirm")}
            </AlertDialogAction>
          </AlertDialogFooterPrimitive>
        </AlertDialogContent>
      </AlertDialog>

      {submissionMode === "immediate" ? <CreateApplicationDialog
        open={createApplicationDialogOpen}
        onOpenChange={setCreateApplicationDialogOpen}
        onApplicationCreated={(option) => {
          const createdApplicationOption: VaccineOption = {
            kind: "item",
            value: `item:${option.id}`,
            label: option.name || "",
            rawName: option.name || "",
            itemId: option.id,
            applicationType: option.product_subtype?.name?.toLowerCase() === "medicamento" ? "medicamento" : "vacina",
            dose_intervals: [],
          };
          setCreatedOption(createdApplicationOption);
          setApplicationSearch(createdApplicationOption.label);
          setValue("item_id", createdApplicationOption.itemId, { shouldDirty: true, shouldTouch: true, shouldValidate: true });
        }}
      /> : null}
    </>
  );
}
